/* dp-hero.js — custom <dp-hero> element. Cross-fades the active image + copy
   group (title/subheading/button), auto-rotates, and supports dot navigation and touch swipe.
   No horizontal sliding. Progressive enhancement: with JS off, slide 1 shows.
   Reads timing from data-speed (seconds) and enable from data-autoplay. */
(function () {
  if (customElements.get('dp-hero')) return;

  class DpHero extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      // autoplay runs only when every one of these reasons is clear
      this.blocked = { hover: false, focus: false, hidden: false };
      // bound handlers, so connect adds and disconnect removes the same refs
      this._onDot = (e) => this.go(Number(e.currentTarget.dataset.index), true);
      this._onEnter = () => this.setBlock('hover', true);
      this._onLeave = () => this.setBlock('hover', false);
      this._onFocusIn = () => this.setBlock('focus', true);
      this._onFocusOut = () => this.setBlock('focus', false);
      this._onVisibility = () => this.setBlock('hidden', document.hidden);
      this._onMotion = () => this.update();
      // swipe: a mostly-horizontal drag of 40px+ changes slide (left = next)
      this._onTouchStart = (e) => {
        const t = e.touches[0];
        this.touch = e.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null;
      };
      this._onTouchEnd = (e) => {
        if (!this.touch) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - this.touch.x;
        const dy = t.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.go(this.index + (dx < 0 ? 1 : -1), true);
      };
      // the copy card's inline height only lives for the resize transition. Also
      // on cancel (breakpoint or reduced motion mid-glide), but not when a newer
      // glide is running: interrupting one fires a late cancel for the old one.
      this._onCopyEnd = (e) => {
        if (e.target !== this.copy || e.propertyName !== 'height') return;
        const gliding = this.copy.getAnimations &&
          this.copy.getAnimations().some((a) => a.transitionProperty === 'height');
        if (!gliding) this.copy.style.height = '';
      };
      this._onSelect = (e) => {
        const el = e.target.closest('[data-index]');
        if (el) this.go(Number(el.dataset.index), true);
      };
    }

    connectedCallback() {
      if (this._ready) return; // guard against a re-connect double-init
      this.groups = Array.from(this.querySelectorAll('.dp-hero__group'));
      this.slides = Array.from(this.querySelectorAll('.dp-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.dp-hero__dot'));
      this.copy = this.querySelector('.dp-hero__copy');
      this.count = this.slides.length;
      this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.autoplay =
        this.dataset.autoplay === 'true' && this.count > 1 && !this.designMode;

      if (this.count <= 1) return;
      this._ready = true;

      // an auto-rotating region should not announce every tick to screen readers
      if (this.copy) {
        this.copy.setAttribute('aria-live', this.autoplay ? 'off' : 'polite');
      }

      this.dots.forEach((dot) => dot.addEventListener('click', this._onDot));
      this.frame = this.querySelector('.dp-hero__frame');
      if (this.frame) {
        this.frame.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.frame.addEventListener('touchend', this._onTouchEnd, { passive: true });
      }
      if (this.copy) {
        this.copy.addEventListener('transitionend', this._onCopyEnd);
        this.copy.addEventListener('transitioncancel', this._onCopyEnd);
      }
      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('visibilitychange', this._onVisibility);
      if (this.motion.addEventListener) {
        this.motion.addEventListener('change', this._onMotion);
      } else if (this.motion.addListener) {
        this.motion.addListener(this._onMotion); // Safari < 14
      }

      // theme editor: don't auto-rotate; jump to whatever slide is selected
      if (this.designMode) {
        this.addEventListener('shopify:block:select', this._onSelect);
      }

      this.update();
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      this.removeEventListener('shopify:block:select', this._onSelect);
      if (this.frame) {
        this.frame.removeEventListener('touchstart', this._onTouchStart);
        this.frame.removeEventListener('touchend', this._onTouchEnd);
      }
      if (this.copy) {
        this.copy.removeEventListener('transitionend', this._onCopyEnd);
        this.copy.removeEventListener('transitioncancel', this._onCopyEnd);
      }
      if (this.dots) {
        this.dots.forEach((dot) =>
          dot.removeEventListener('click', this._onDot)
        );
      }
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.motion && this.motion.removeEventListener) {
        this.motion.removeEventListener('change', this._onMotion);
      } else if (this.motion && this.motion.removeListener) {
        this.motion.removeListener(this._onMotion);
      }
      this._ready = false;
    }

    setBlock(reason, on) {
      this.blocked[reason] = on;
      this.update();
    }

    // reconcile the interval with the current allowed state
    update() {
      const allowed =
        this.autoplay &&
        !this.motion.matches &&
        !this.blocked.hover &&
        !this.blocked.focus &&
        !this.blocked.hidden;
      if (allowed) this.play();
      else this.stop();
    }

    go(target, userInitiated) {
      const next = ((target % this.count) + this.count) % this.count;
      if (next !== this.index) {
        this.index = next;
        this.resizeCopy(() =>
          this.groups.forEach((g, n) => this.setActive(g, n === next))
        );
        this.slides.forEach((s, n) => this.setActive(s, n === next));
        this.dots.forEach((d, n) => {
          const on = n === next;
          d.classList.toggle('is-active', on);
          if (on) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }
      // manual nav resets the interval so the next auto-advance is a full cycle
      if (userInitiated) {
        this.stop();
        this.update();
      }
    }

    // Mobile: the frosted card hugs the active copy, so a slide with more lines
    // would snap its top edge. Swap the copy, then animate the card from the old
    // height to the new one (CSS transitions it). Desktop reserves the tallest
    // slide in a grid cell, so the heights match and this is a no-op there.
    resizeCopy(swap) {
      const copy = this.copy;
      if (!copy) return swap();
      const from = copy.getBoundingClientRect().height; // mid-transition, this is the current height
      copy.style.height = '';
      swap();
      const to = copy.getBoundingClientRect().height;
      if (this.motion.matches || Math.abs(from - to) < 1) return;
      copy.style.height = from + 'px';
      void copy.offsetHeight; // commit the start height before the transition
      copy.style.height = to + 'px';
    }

    setActive(el, on) {
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      // aria-hidden alone doesn't untab a child link — keep hidden CTAs out of tab order
      const link = el.querySelector('a');
      if (link) {
        if (on) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      }
    }

    next() {
      this.go(this.index + 1, false);
    }

    play() {
      if (!this.timer) {
        this.timer = window.setInterval(() => this.next(), this.speed);
      }
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('dp-hero', DpHero);
})();
